import React from "react";
import styled from "styled-components";

import {
  Card,
  HorizontalLine,
  RowContainer,
} from "../../../../components/containers/styledComponents";
import { CompassParagraph } from "../../../../components/common/styledComponents";
import {
  darkGreen,
  greyText,
  lightGreyOutline,
  lightGreyText,
} from "../../../../styles";
import { EvidenceChips, ExpandableJudgement, GradeChip } from "./assessmentAtoms";
import { ConfidenceRing } from "./ScoreRing";
import { PillarRow } from "./assessmentViewModel";

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const Row = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px 0;
  min-width: 0;

  & + & {
    border-top: 1px solid ${lightGreyOutline};
  }
`;

const Index = styled.span`
  width: 18px;
  flex-shrink: 0;
  padding-top: 3px;
  font-family: "ApercuMono", "Apercu", monospace;
  font-size: 11px;
  color: ${lightGreyText};
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
`;

const HeadLine = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const Name = styled.span`
  font-family: "ApercuMedium";
  font-size: 14px;
  color: ${darkGreen};
  white-space: nowrap;
`;

const Spacer = styled.span`
  flex: 1;
`;

export const PillarTelemetryPanel = ({
  rows,
  m,
  isLoading,
}: PillarTelemetryPanelProps): JSX.Element => (
  <Card
    isLoading={isLoading}
    h="520px"
    m={m}
    style={{ display: "flex", flexDirection: "column", minHeight: 0 }}
  >
    <RowContainer mt={0} mb={0} jc="space-between" ai="center">
      <CompassParagraph fontSize={16} bold color={darkGreen}>
        Pillar Telemetry
      </CompassParagraph>
      <CompassParagraph fontSize={12} color={greyText}>
        {rows.length} pillars
      </CompassParagraph>
    </RowContainer>
    <HorizontalLine color="#E2E8F0" marginTop={4} marginBottom={0} />

    <ScrollArea>
      {rows.map((row) => (
        <Row key={row.pillar}>
          <Index>{String(row.index).padStart(2, "0")}</Index>
          <Body>
            <HeadLine>
              <Name>{row.label}</Name>
              <Spacer />
              <GradeChip state={row.state} />
              {row.confidence && (
                <ConfidenceRing confidence={row.confidence} size="sm" />
              )}
            </HeadLine>
            <ExpandableJudgement text={row.judgement} maxLines={2} />
            {row.evidenceRefs.length > 0 && (
              <EvidenceChips refs={row.evidenceRefs} max={2} />
            )}
          </Body>
        </Row>
      ))}
    </ScrollArea>
  </Card>
);

type PillarTelemetryPanelProps = {
  rows: PillarRow[];
  m?: string;
  isLoading: boolean;
};
